import { defaultExpiryDateIso, expiryDateToMillis } from './hostedReport.js';
import {
  HOSTED_REPORT_DEFAULT_EXPIRY_DAYS,
  type HostedReportSummary,
} from '../types/hostedReport.js';

// Tools → HTML Hosting — the pure diffing behind the edit dialog.
//
// Kept out of the component so "what am I about to change, and what will the
// client see" is testable. The dialog sends ONLY changed fields, which is what
// makes a partial save safe: an untouched password or file is absent from the
// payload rather than sent back as itself.

export type PasswordMode = 'keep' | 'set' | 'clear';

export type HostedReportEditForm = {
  title: string;
  /** '' = no client account. */
  accountId: string;
  /** `yyyy-mm-dd`. Ignored while `neverExpires` is ticked. */
  expiryDate: string;
  /**
   * The "Never expire" tick. ⚠️ While it is ticked the date is IGNORED rather
   * than cleared, so unticking brings back the date the AM had — the same
   * keep-the-typed-value posture as the composer's override tick-boxes.
   */
  neverExpires: boolean;
  passwordMode: PasswordMode;
  passwordValue: string;
  /** A replacement file, or null to keep the current one. */
  file: { filename: string; html: string } | null;
};

export type HostedReportPatch = {
  title?: string;
  accountId?: string | null;
  expiry?: { action: 'set'; atMs: number } | { action: 'never' };
  password?: { action: 'keep' } | { action: 'set'; value: string } | { action: 'clear' };
  file?: { filename: string; html: string };
};

/** The form pre-filled from a report — what "unchanged" looks like. */
export function formFromReport(
  report: HostedReportSummary,
  nowMs: number = Date.now(),
): HostedReportEditForm {
  return {
    title: report.title,
    accountId: report.accountId ?? '',
    // The stored expiry is the END of a local day, so its date part is the day
    // the AM originally picked. A never-expiring report has no date, so the
    // (hidden) field is seeded with the create default — which is what the AM
    // sees if they untick "Never expire".
    expiryDate:
      report.expiresAt === null
        ? defaultExpiryDateIso(nowMs, HOSTED_REPORT_DEFAULT_EXPIRY_DAYS)
        : localDateIso(report.expiresAt),
    neverExpires: report.expiresAt === null,
    passwordMode: 'keep',
    passwordValue: '',
    file: null,
  };
}

function localDateIso(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * The payload for a save: only the fields that actually differ.
 *
 * ⚠️ `passwordMode: 'keep'` emits NOTHING rather than `{action:'keep'}` — the
 * two are equivalent to the server, but omitting it keeps the audit diff and
 * the change summary honest about what the AM touched.
 */
export function buildHostedReportPatch(
  report: HostedReportSummary,
  form: HostedReportEditForm,
): HostedReportPatch {
  const patch: HostedReportPatch = {};

  const title = form.title.trim();
  if (title && title !== report.title) patch.title = title;

  const accountId = form.accountId || null;
  if (accountId !== report.accountId) patch.accountId = accountId;

  const currentMs = report.expiresAt === null ? null : Date.parse(report.expiresAt);
  if (report.portalLibraryEntryId !== null) {
    // ⚠️⚠️ A PORTAL-LINKED report does not own its expiry (Amendment 2 §A) — the
    // tile's end date does, and the server refuses a change with a 409. The
    // dialog shows no expiry control for one; this is the second line, so a
    // form that somehow still carried a date cannot emit an expiry change.
  } else if (form.neverExpires) {
    // ⚠️ Only when it CHANGES — a never-expiring report re-saved as never emits
    // nothing, so opening the dialog and pressing save writes no edit.
    if (currentMs !== null) patch.expiry = { action: 'never' };
  } else {
    const expiresAtMs = expiryDateToMillis(form.expiryDate);
    if (expiresAtMs !== null && expiresAtMs !== currentMs) {
      patch.expiry = { action: 'set', atMs: expiresAtMs };
    }
  }

  if (form.passwordMode === 'set' && form.passwordValue) {
    patch.password = { action: 'set', value: form.passwordValue };
  } else if (form.passwordMode === 'clear' && report.hasPassword) {
    patch.password = { action: 'clear' };
  }

  if (form.file) patch.file = form.file;

  return patch;
}

export function hasChanges(patch: HostedReportPatch): boolean {
  return Object.keys(patch).length > 0;
}

/**
 * Plain-English lines for the save confirmation.
 *
 * ⚠️ This is a LIVE, ALREADY-DISTRIBUTED URL. The AM needs to see what a client
 * holding the link will experience, not a generic "save changes?" — especially
 * for a file replace, where the client's next load silently shows different
 * content (the `no-store` header means there is no cache to wait out).
 */
export function describeHostedReportChanges(
  report: HostedReportSummary,
  patch: HostedReportPatch,
  accountLabel: (id: string) => string,
): string[] {
  const lines: string[] = [];
  if (patch.file) {
    lines.push(`Replace the file with “${patch.file.filename}” — clients see the new version on their next load.`);
  }
  if (patch.title !== undefined) {
    lines.push(`Retitle it “${patch.title}” (internal only — clients don’t see the title).`);
  }
  if (patch.accountId !== undefined) {
    lines.push(
      patch.accountId
        ? `Link it to ${accountLabel(patch.accountId)}.`
        : 'Remove the client-account link.',
    );
  }
  if (patch.expiry?.action === 'never') {
    lines.push('Remove the expiry — the link keeps working until it is revoked.');
  }
  if (patch.expiry?.action === 'set') {
    const atMs = patch.expiry.atMs;
    const past = atMs <= Date.now();
    if (past) {
      lines.push('Set the expiry in the past — the link stops working straight away.');
    } else if (report.expiresAt === null) {
      // "Shorten" and "Extend" both compare against a date that doesn't exist.
      lines.push(`Give it an expiry — it stops working after ${formatDay(atMs)}.`);
    } else {
      const shortening = atMs < Date.parse(report.expiresAt);
      lines.push(`${shortening ? 'Shorten' : 'Extend'} the expiry to ${formatDay(atMs)}.`);
    }
  }
  if (patch.password?.action === 'set') {
    lines.push(
      report.hasPassword
        ? 'Change the password — anyone using the old one, including viewers already reading it, loses access.'
        : 'Add a password — anyone already reading it loses access until they enter the new one.',
    );
  }
  if (patch.password?.action === 'clear') {
    lines.push('Remove the password — anyone with the link can then open it without one.');
  }
  return lines;
}

function formatDay(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * ⚠️⚠️ THE PASSWORD ON A PORTAL-LINKED REPORT (Round 4, amended §2). Two doors,
 * two rules, said in the words of the door it guards: the portal checks who a
 * client is and never asks for the password; the password applies only to the
 * shareable link — which is permanent now, so this is the only control it has.
 */
export const PORTAL_PASSWORD_LINK_ONLY_NOTE =
  'This report is also on the Kyck portal. The portal checks who you are and never asks for this password — it only applies to the shareable link.';

/**
 * The password control's options.
 *
 * ⚠️ "Remove the password" is offered ONLY when there is one to remove — an
 * always-present Remove on an open report is an action that does nothing, and
 * the AM has to try it to find that out. The 'keep' label likewise differs: on
 * an open report it reads "No password", which is the true current state rather
 * than an instruction to keep something that isn't there.
 */
export function passwordModeOptions(
  hasPassword: boolean,
): Array<{ value: PasswordMode; label: string }> {
  return [
    { value: 'keep', label: hasPassword ? 'Keep the current password' : 'No password' },
    { value: 'set', label: hasPassword ? 'Change the password' : 'Add a password' },
    ...(hasPassword
      ? [{ value: 'clear' as PasswordMode, label: 'Remove the password' }]
      : []),
  ];
}

/**
 * True while the AM is adding a password to a link that is currently OPEN.
 *
 * Worth saying out loud in the dialog: the link has been shared and is readable
 * by anyone holding it right now, and stays that way until they press save.
 */
export function isClosingAnOpenLink(
  report: HostedReportSummary,
  form: HostedReportEditForm,
): boolean {
  return !report.hasPassword && form.passwordMode === 'set' && form.passwordValue.length > 0;
}
